import type {
  ListingsAPIResponseNumericAttribute,
  ListingsAPIResponsePagination,
  ListingsAPIResponseProduct,
  ListingsAPIResponseTextAttribute,
} from '@noths/polaris-client-listings-api';
import type { CurrencyCode } from '@noths/polaris-client-localisation';

import type { RequestProductsActionProductsPayload } from 'src/redux/products/types';
import type { Product, ProductDelivery } from 'src/redux/products/types/Product';
import { PRODUCT_IMAGE_LIMIT } from 'src/redux/products/types/Product';
import type { Price } from 'src/types/price';

interface AdaptResponseProductsToStoreArgs {
  currencyCode: CurrencyCode;
  metadata: { attributionToken: string; filter?: string; sponsored?: { nextOffset: number } };
  /**
   * The batch this response answers. A batch after one that carried sponsored
   * products starts at the organic cursor rather than at a page boundary, so the
   * API's own page arithmetic can name the wrong page and overwrite the batch
   * before it. Absent, the API's page is used.
   */
  page?: number;
  pagination: ListingsAPIResponsePagination;
  products: ListingsAPIResponseProduct[];
  term: string;
}

export const adaptResponseProductsToStore = ({
  currencyCode,
  metadata,
  page,
  pagination,
  products,
  term,
}: AdaptResponseProductsToStoreArgs): RequestProductsActionProductsPayload => {
  const category = null;
  const navigation: unknown[] = [];
  const providerMetadata = metadata.filter ? { filter: metadata.filter } : null;
  const partner = null;
  const providerAttributionToken = metadata.attributionToken || null;

  const {
    currentPage,
    pageSize: perPage,
    resultsReturnedThisPage: productsReturnedCount,
    totalResults: totalProducts,
  } = pagination;

  const totalPages = Math.ceil(totalProducts / perPage);

  const adaptedProducts = products.map((product) => {
    const {
      attributes,
      available,
      code,
      delivery: productDelivery,
      images,
      isExpandedSearch,
      isNew,
      links,
      onSale,
      partner: { name: partnerName, shortcode: partnerShortcode },
      preSalePrices,
      price,
      purchasable,
      ratings,
      salePercentage,
      sponsoredProductMetadata,
      title,
    } = product;

    // The listings API chooses the option and returns both halves or neither, so a
    // card's cost and its lead time always describe the same one. Nothing is
    // derived here from `attributes.delivery_*`: those are per-type values whose
    // aggregates answer different questions, and pairing two of them is the fault
    // this replaces. An absent `delivery` therefore means no delivery line, not a
    // reason to fall back.
    //
    // There is no delivery zone check here either. The request names the zone
    // (`adaptStoreToRequestBody`), so a product carrying an option is one the API
    // has already decided we can quote for that customer. Re-checking here would
    // be a second opinion formed from less information than the server has.
    //
    // `type` is dropped deliberately. The card names no delivery option, and the
    // store is serialised into every page that lists the product.
    const adaptedDelivery: ProductDelivery | null = productDelivery
      ? { cost: productDelivery.cost, days: productDelivery.days }
      : null;

    // `attributes` is a passthrough of every Vertex custom attribute, including
    // commercially sensitive ones (e.g. `actual_comms_rate`), so it is never
    // spread. Only named keys are read.
    //
    // `isHighlyRated`/`isTopBrand` from `@noths/polaris-client-listings-api` look like
    // the obvious reuse, but they are typed for the recommendations endpoint, whose
    // `ITextAttribute.text` and `INumberAttribute.numbers` are required where this
    // endpoint's are optional. Neither union member is assignable, so calling them
    // here is a compile error, not a style choice. The cast below is narrower than
    // the one that would be needed to paper over that schema difference.
    const highlyRated =
      (attributes?.highly_rated as ListingsAPIResponseTextAttribute)?.text?.[0] === 'true';
    // Named `star_brand` in the catalogue; there is no `top_brand` attribute.
    const starBrand =
      (attributes?.star_brand as ListingsAPIResponseTextAttribute)?.text?.[0] === 'true';
    // The Talon One campaigns the product is in, matched against authored promo labels.
    // Read from either variant because Talon One IDs are numeric while the attribute is
    // expected as text, and stringified and trimmed so both compare equal to an ID typed
    // into Contentstack.
    const promotions = attributes?.promotions as
      | (ListingsAPIResponseTextAttribute & ListingsAPIResponseNumericAttribute)
      | undefined;
    const campaignIds = [...(promotions?.text ?? []), ...(promotions?.numbers ?? [])]
      .map((id) => String(id).trim())
      .filter(Boolean);
    // `Number.isFinite` covers every way the API can fail to give us a number:
    // absent, null, or a non-numeric value. Zero is excluded deliberately: the API
    // returns an average of 0 alongside a count of 0 for a product nobody has
    // reviewed, and the card would render that as a "0" rating rather than as no
    // rating at all.
    const averageRating = ratings?.averageRating;
    const hasRating =
      typeof averageRating === 'number' && Number.isFinite(averageRating) && averageRating > 0;

    // Allowlist the fields that are actually read during initial render, tracking
    // or SEO. Anything else from the API response (e.g. `prices`,
    // `inventoryStatus`, `partner.links`) is intentionally dropped so it is not
    // serialised into the initial page source.
    //
    // `images` is the one field kept as an array rather than reduced to a single
    // value. The order is carried deliberately, so the card can show more than the
    // primary image, but only up to `PRODUCT_IMAGE_LIMIT`: the API puts no bound on
    // the array and the card renders no more than that, so the rest would be
    // serialised into every page and then discarded at render. The slice comes after
    // the empty hrefs are dropped, so a blank entry near the front costs the product
    // a placeholder rather than a real image. The per-image `title` and `gravity` are
    // still dropped: the title duplicates `title` below, and nothing reads the gravity.
    const adaptedProduct: Product = {
      ...(hasRating ? { averageRating } : {}),
      available,
      ...(campaignIds.length > 0 ? { campaignIds } : {}),
      code,
      delivery: adaptedDelivery,
      detailPageHref: links?.[0]?.rel || '',
      // Omitted rather than defaulted, as with `averageRating` above and `starBrand`
      // below, so a product without them adds nothing to the serialised page.
      ...(highlyRated ? { highlyRated } : {}),
      images:
        images
          ?.map(({ href }) => href)
          .filter(Boolean)
          .slice(0, PRODUCT_IMAGE_LIMIT) ?? [],
      isExpandedSearch,
      isNew,
      onSale,
      // The listings API can return a partner with a missing/undefined `name` or
      // `shortcode` (e.g. inactive partners). Coerce to '' so no `undefined`
      // reaches the Redux store — Next.js cannot serialize `undefined` values in
      // `getServerSideProps`.
      partner: { name: partnerName ?? '', shortcode: partnerShortcode ?? '' },
      // The API types `currency` as a plain string; the store narrows it to a
      // CurrencyCode union. This narrowing was previously implicit via the
      // `as Product` cast applied to the whole spread object.
      preSalePrices: preSalePrices as Price[],
      price: price as Price,
      // The API types `purchasable` as required but omits it for products whose
      // catalogue attribute is unpopulated, and `undefined` cannot be serialized
      // out of `getServerSideProps`, so one such product took the whole page down.
      //
      // Defaulted to purchasable rather than to `false`: across 600 QA products the
      // flag was only ever `true` or absent, never `false`, and every product
      // missing it was `available`. Absence is missing data, not an unbuyable
      // product, and reading it as `false` would mark a quarter of a listing "Out of
      // stock" and drop each of those cards' delivery lines.
      purchasable: purchasable ?? true,
      salePercentage,
      // Only the two fields the events need; `isSponsored` is derived from its presence.
      ...(sponsoredProductMetadata
        ? {
            sponsored: {
              adId: sponsoredProductMetadata.adId,
              adUnitId: sponsoredProductMetadata.adUnitId,
            },
          }
        : {}),
      ...(starBrand ? { starBrand } : {}),
      title,
    };

    return adaptedProduct;
  });

  const result = {
    category,
    currencyCode,
    products: adaptedProducts,
    perPage,
    lastPageLoaded: page ?? currentPage,
    ...(metadata.sponsored ? { organicNextOffset: metadata.sponsored.nextOffset } : {}),
    totalPages,
    totalProducts,
    productsReturnedCount,
    term,
    navigation,
    partner,
    providerAttributionToken,
    providerMetadata,
  };
  return result;
};
